<!--
  강사 권한 요청 — 내 요청 상태 화면
  · GET /instructor/access-request/me 결과에 따라 분기
    (강사 활성 / 이력 없음 / 대기중 / 승인됨(재로그인 안내) / 거절됨(사유))
  · learner 계정의 기본 랜딩 화면 (main.js 가드 참고)
  · route meta.layout='empty' — 독립 카드 레이아웃
-->
<template>
  <div class="p-access">
    <div class="p-access__card">
      <!-- 로고 -->
      <div class="p-access__logo">
        <img src="/favicon.png" alt="Pineapple" />
        <div>
          <div class="p-access__brand">pineapple</div>
          <div class="p-access__brand-sub">INSTRUCTOR</div>
        </div>
      </div>

      <template v-if="loading">
        <div class="p-access__loading">요청 상태를 확인하는 중입니다…</div>
      </template>

      <template v-else-if="loadError">
        <h1 class="p-access__title">요청 상태 조회 실패</h1>
        <p class="p-access__desc">요청 상태를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>
        <a-btn kind="primary" class="p-access__cta" @click="fetchStatus">다시 시도</a-btn>
      </template>

      <!-- 이미 승인된 강사 -->
      <template v-else-if="userType === 'instructor'">
        <a-pill kind="ok" class="p-access__pill">강사 활성</a-pill>
        <h1 class="p-access__title">강사 권한이 활성화되어 있습니다</h1>
        <p class="p-access__desc">모든 강사 메뉴를 이용하실 수 있습니다.</p>
        <a-btn kind="primary" class="p-access__cta" @click="$router.push({ name: 'Sales' })">대시보드로 이동</a-btn>
      </template>

      <!-- 요청 이력 없음 -->
      <template v-else-if="!request">
        <a-pill class="p-access__pill">요청 이력 없음</a-pill>
        <h1 class="p-access__title">강사 권한 요청 이력이 없습니다</h1>
        <p class="p-access__desc">
          강사 인증 정보를 제출하시면 관리자 확인 후<br />
          강사 권한이 부여됩니다.
        </p>
        <a-btn kind="primary" class="p-access__cta" @click="$router.push({ name: 'InstructorAccessRequest' })">강사 권한 요청하기</a-btn>
      </template>

      <!-- 대기중 -->
      <template v-else-if="request.status === 'request'">
        <a-pill kind="warn" class="p-access__pill">대기중</a-pill>
        <h1 class="p-access__title">강사 권한 요청이 접수되었습니다</h1>
        <p class="p-access__desc">관리자 확인 후 처리 결과를 메일로 안내드립니다.</p>
        <dl class="p-access__summary">
          <div><dt>소속 기관</dt><dd>{{ request.org_name || '-' }}</dd></div>
          <div><dt>직위</dt><dd>{{ request.position || '-' }}</dd></div>
          <div v-if="request.subject"><dt>담당 과목·교재</dt><dd>{{ request.subject }}</dd></div>
          <div><dt>요청일시</dt><dd>{{ $formatDate(request.created_at, 'YYYY-mm-dd HH:MM') }}</dd></div>
        </dl>
      </template>

      <!-- 승인됨 (구 토큰 — 재로그인 필요) -->
      <template v-else-if="request.status === 'approval'">
        <a-pill kind="ok" class="p-access__pill">승인됨</a-pill>
        <h1 class="p-access__title">강사 권한 승인이 완료되었습니다</h1>
        <p class="p-access__desc">
          강사 메뉴를 이용하려면 <b>다시 로그인</b>해주세요.<br />
          (기존 로그인 정보에는 승인 전 권한이 남아 있습니다)
        </p>
        <a-btn kind="primary" class="p-access__cta" @click="relogin">다시 로그인</a-btn>
      </template>

      <!-- 거절됨 -->
      <template v-else-if="request.status === 'rejection'">
        <a-pill kind="err" class="p-access__pill">거절됨</a-pill>
        <h1 class="p-access__title">강사 권한 요청이 거절되었습니다</h1>
        <div v-if="request.reject_reason" class="p-access__reason">
          <div class="p-access__reason-label">거절 사유</div>
          <div class="p-access__reason-text">{{ request.reject_reason }}</div>
        </div>
        <p class="p-access__desc">정보를 보완하여 다시 요청하실 수 있습니다.</p>
        <a-btn kind="primary" class="p-access__cta" @click="$router.push({ name: 'InstructorAccessRequest' })">다시 요청하기</a-btn>
      </template>

      <!-- 하단 -->
      <div class="p-access__footer">
        <span class="p-mono">{{ userEmail }}</span>
        <a class="p-access__link" @click.prevent="logout" href="#">로그아웃</a>
      </div>
    </div>
  </div>
</template>

<script>
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';

export default {
  name: 'InstructorAccessStatus',
  components: { ABtn, APill },
  data() {
    return {
      loading: true,
      loadError: false,
      request: null,
      userType: null,
    };
  },
  computed: {
    userEmail() {
      return this.$store.getters['auth/getUserInfo']?.email || '';
    },
  },
  mounted() {
    this.fetchStatus();
  },
  methods: {
    async fetchStatus() {
      this.loading = true;
      this.loadError = false;
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/instructor/access-request/me',
        });
        this.request = res.data.request;
        this.userType = res.data.user_type;
      } catch (e) {
        console.error('요청 상태 조회 실패:', e);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    relogin() {
      // 승인 반영을 위해 토큰 폐기 후 재로그인 (가이드: 승인 직후 토큰 재발급 필요)
      this.$store.dispatch('auth/logout');
    },
    logout() {
      this.$store.dispatch('auth/logout');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';
@import './access-shared.scss';
</style>
